import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, RefreshCw, Pencil, Bell,
  Users, Trophy, HelpCircle, Clock, Zap, Key,
  ExternalLink, Copy, ChevronRight, CheckCircle,
  Timer, Repeat, Download, Send, Phone
} from 'lucide-react';

const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// Notification metadata with detailed descriptions
const NOTIFICATION_META = {
  // Instant triggers
  player_invite: { 
    name: 'Player Invite', 
    category: 'Onboarding', 
    type: 'Instant', 
    icon: Users,
    description: 'Sent when a league admin invites someone to join their league'
  },
  welcome_email_player: { 
    name: 'Welcome Email - Players', 
    category: 'Onboarding', 
    type: 'Instant', 
    icon: Mail,
    description: 'Sent when a player first joins a league (accepts invite)'
  },
  welcome_email_admin: { 
    name: 'Welcome Email - League Admin', 
    category: 'Onboarding', 
    type: 'Instant', 
    icon: Mail,
    description: 'Sent when a league admin creates a new league'
  },
  season_premiere: { 
    name: 'Season Premiere', 
    category: 'Onboarding', 
    type: 'Manual', 
    icon: Bell,
    description: 'Pre-season briefing sent before the season premiere - manually triggered by Super Admin'
  },
  draft_started: { 
    name: 'Draft Started', 
    category: 'Draft', 
    type: 'Instant', 
    icon: Zap,
    description: 'Sent to all players when the league admin opens the draft'
  },
  draft_turn: { 
    name: 'Your Draft Turn', 
    category: 'Draft', 
    type: 'Instant', 
    icon: Clock,
    description: 'Sent to a player when it becomes their turn to pick'
  },
  draft_completed: { 
    name: 'Draft Completed', 
    category: 'Draft', 
    type: 'Instant', 
    icon: CheckCircle,
    description: 'Sent to all players when the draft finishes and teams are set'
  },
  bonus_question_available: { 
    name: 'Bonus Question Available', 
    category: 'Gameplay', 
    type: 'Instant', 
    icon: HelpCircle,
    description: 'Sent when a new bonus question is published for the upcoming episode'
  },
  bonus_question_reminder: { 
    name: 'Bonus Question Reminder', 
    category: 'Gameplay', 
    type: 'Scheduled', 
    icon: Timer,
    description: 'Sent 1 hour before the bonus question deadline to players who haven\'t answered'
  },
  episode_scored: { 
    name: 'Episode Scored', 
    category: 'Gameplay', 
    type: 'Instant', 
    icon: Trophy,
    description: 'Sent to all players after an episode is scored with their points and rankings'
  },
  password_reset: { 
    name: 'Password Reset', 
    category: 'Account', 
    type: 'Instant', 
    icon: Key, 
    essential: true,
    description: 'Sent when a user requests to reset their password'
  },
  // Multi-step (drips)
  drip_no_league: { 
    name: 'No League Created', 
    category: 'Onboarding', 
    type: 'Multi-Step', 
    icon: Repeat,
    description: 'Sent to new admins who signed up but haven\'t created a league yet'
  },
  drip_no_season: { 
    name: 'No Season Created', 
    category: 'Onboarding', 
    type: 'Multi-Step', 
    icon: Repeat,
    description: 'Sent to league admins who have a league but haven\'t created a season'
  },
  drip_no_invites: { 
    name: 'No Players Invited', 
    category: 'Onboarding', 
    type: 'Multi-Step', 
    icon: Repeat,
    description: 'Sent to league admins who have a season but haven\'t invited any players'
  },
};

const CATEGORIES = ['Onboarding', 'Draft', 'Gameplay', 'Account'];
const SMS_CHAR_LIMIT = 320;

export default function CommsNotifications() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Send Test state
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testNotification, setTestNotification] = useState(null);
  const [testChannel, setTestChannel] = useState('email');
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testStep, setTestStep] = useState(1);
  const [sendingTest, setSendingTest] = useState(false);

  // Broadcast state (for manual triggers like season_premiere)
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastNotification, setBroadcastNotification] = useState(null);
  const [broadcastSeasonIds, setBroadcastSeasonIds] = useState([]);
  const [broadcastChannel, setBroadcastChannel] = useState('email');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [seasons, setSeasons] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    email_enabled: false,
    template_id: '',
    sms_enabled: false,
    sms_message: '',
    send_to: 'affected_player',
    delay_minutes: 0,
    // Multi-step fields
    steps: []
  });

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [triggersRes, dripsRes] = await Promise.all([
        axios.get(`${API}/platform/email-triggers`, getAuthConfig()),
        axios.get(`${API}/platform/drip-campaigns`, getAuthConfig()).catch(() => ({ data: [] }))
      ]);

      // Transform triggers
      const triggerNotifications = (triggersRes.data || []).map(t => ({
        id: t.event_id,
        ...NOTIFICATION_META[t.event_id],
        email_enabled: t.email_enabled ?? t.is_enabled ?? false,
        template_id: t.template_id || '',
        sms_enabled: t.sms_enabled ?? false,
        sms_message: t.sms_message || '',
        send_to: t.send_to || 'affected_player',
        delay_minutes: t.delay_minutes || 0,
        variables: t.variables,
        isMultiStep: false
      }));

      // Transform drip campaigns
      const dripNotifications = (dripsRes.data || []).map(d => ({
        id: d.id,
        ...NOTIFICATION_META[d.id],
        name: d.name || NOTIFICATION_META[d.id]?.name || d.id,
        is_enabled: d.is_enabled,
        email_enabled: d.email_enabled ?? true,
        sms_enabled: d.sms_enabled ?? false,
        steps: [
          {
            step: 1,
            delay_days: d.days_before_email_1 || 3,
            email_template_id: d.template_id_email_1 || '',
            sms_message: d.sms_message_1 || ''
          },
          {
            step: 2,
            delay_days: d.days_between_emails || 4,
            email_template_id: d.template_id_email_2 || '',
            sms_message: d.sms_message_2 || ''
          }
        ],
        isMultiStep: true,
        rawData: d
      }));

      setNotifications([...triggerNotifications, ...dripNotifications]);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [API]);

  const syncTriggers = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/platform/email-triggers/sync`, {}, getAuthConfig());
      const { added, removed } = response.data;
      
      if (added.length > 0 || removed.length > 0) {
        toast.success(`Synced! Added: ${added.length}, Removed: ${removed.length}`);
        // Reload notifications to show the new triggers
        await loadNotifications();
      } else {
        toast.info('All triggers are up to date');
      }
    } catch (error) {
      console.error('Failed to sync triggers:', error);
      toast.error('Failed to sync triggers');
    } finally {
      setSyncing(false);
    }
  };

  // Load seasons for broadcast dialog
  const loadSeasons = async () => {
    try {
      const response = await axios.get(`${API}/platform/seasons`, getAuthConfig());
      setSeasons(response.data || []);
    } catch (error) {
      console.error('Failed to load seasons:', error);
      // Fallback: try loading from tenants
      try {
        const tenantsRes = await axios.get(`${API}/platform/tenants`, getAuthConfig());
        const allSeasons = [];
        for (const tenant of tenantsRes.data || []) {
          if (tenant.seasons) {
            allSeasons.push(...tenant.seasons.map(s => ({ ...s, tenant_name: tenant.name })));
          }
        }
        setSeasons(allSeasons);
      } catch (e) {
        console.error('Failed to load seasons from tenants:', e);
      }
    }
  };

  // Open broadcast dialog for manual triggers
  const openBroadcastDialog = (notification, e) => {
    e.stopPropagation();
    setBroadcastNotification(notification);
    setBroadcastSeasonIds([]);
    setBroadcastChannel(hasEmail(notification) ? 'email' : 'sms');
    setShowBroadcastDialog(true);
    loadSeasons();
  };

  // Toggle season selection
  const toggleSeasonSelection = (seasonId) => {
    setBroadcastSeasonIds(prev => 
      prev.includes(seasonId)
        ? prev.filter(id => id !== seasonId)
        : [...prev, seasonId]
    );
  };

  // Select/Deselect all seasons
  const toggleSelectAll = () => {
    if (broadcastSeasonIds.length === seasons.length) {
      setBroadcastSeasonIds([]);
    } else {
      setBroadcastSeasonIds(seasons.map(s => s.id));
    }
  };

  // Handle broadcast to all players in selected seasons
  const handleBroadcast = async () => {
    if (broadcastSeasonIds.length === 0) {
      toast.error('Please select at least one season');
      return;
    }
    
    try {
      setSendingBroadcast(true);
      const response = await axios.post(
        `${API}/platform/email-triggers/${broadcastNotification.id}/broadcast`,
        {
          season_ids: broadcastSeasonIds,
          channel: broadcastChannel
        },
        getAuthConfig()
      );
      
      const result = response.data;
      toast.success(`Broadcast sent to ${result.total_recipients} players across ${result.seasons_count} season(s)! (${result.total_emails_sent || 0} emails, ${result.total_sms_sent || 0} SMS)`);
      setShowBroadcastDialog(false);
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSendingBroadcast(false);
    }
  };

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadNotifications();
  }, [user, navigate, loadNotifications]);

  const handleEdit = (notification) => {
    setSelectedNotification(notification);
    if (notification.isMultiStep) {
      setFormData({
        id: notification.id,
        is_enabled: notification.is_enabled,
        email_enabled: notification.email_enabled ?? true,
        sms_enabled: notification.sms_enabled ?? false,
        steps: notification.steps || []
      });
    } else {
      setFormData({
        id: notification.id,
        email_enabled: notification.email_enabled,
        template_id: notification.template_id,
        sms_enabled: notification.sms_enabled,
        sms_message: notification.sms_message,
        send_to: notification.send_to,
        delay_minutes: notification.delay_minutes
      });
    }
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (selectedNotification.isMultiStep) {
        // Save drip campaign
        await axios.patch(`${API}/platform/drip-campaigns/${formData.id}`, {
          is_enabled: formData.is_enabled,
          email_enabled: formData.email_enabled,
          sms_enabled: formData.sms_enabled,
          days_before_email_1: formData.steps[0]?.delay_days,
          template_id_email_1: formData.steps[0]?.email_template_id,
          sms_message_1: formData.steps[0]?.sms_message,
          days_between_emails: formData.steps[1]?.delay_days,
          template_id_email_2: formData.steps[1]?.email_template_id,
          sms_message_2: formData.steps[1]?.sms_message
        }, getAuthConfig());
      } else {
        // Save trigger
        if (formData.email_enabled && !formData.template_id) {
          toast.error('Please provide a SendGrid Template ID');
          setSaving(false);
          return;
        }
        if (formData.sms_enabled && !formData.sms_message.trim()) {
          toast.error('Please provide an SMS message');
          setSaving(false);
          return;
        }
        
        await axios.patch(`${API}/platform/email-triggers/${formData.id}`, {
          email_enabled: formData.email_enabled,
          is_enabled: formData.email_enabled,
          template_id: formData.template_id,
          sms_enabled: formData.sms_enabled,
          sms_message: formData.sms_message,
          send_to: formData.send_to,
          delay_minutes: formData.delay_minutes
        }, getAuthConfig());
      }
      
      toast.success('Notification saved');
      setShowEditDialog(false);
      loadNotifications();
    } catch (error) {
      toast.error('Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const getTypeIcon = (type) => {
    if (type === 'Multi-Step') return Repeat;
    if (type === 'Scheduled') return Timer;
    return Zap;
  };

  const getTypeBadgeStyle = (type) => {
    if (type === 'Multi-Step') return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    if (type === 'Scheduled') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    return 'bg-zinc-700 text-zinc-400 border-zinc-600';
  };

  const isActive = (n) => {
    if (n.isMultiStep) return n.is_enabled;
    return n.email_enabled || n.sms_enabled;
  };

  const hasEmail = (n) => {
    if (!n) return false;
    if (n.isMultiStep) return n.steps?.some(s => s.email_template_id);
    return !!n.template_id;
  };

  const hasSms = (n) => {
    if (!n) return false;
    if (n.isMultiStep) return n.steps?.some(s => s.sms_message);
    return !!n.sms_message;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const insertVariable = (varName) => {
    setFormData(prev => ({
      ...prev,
      sms_message: prev.sms_message + `{{${varName}}}`
    }));
  };

  const updateStep = (stepIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === stepIndex ? { ...s, [field]: value } : s)
    }));
  };

  const openTestDialog = (notification, e) => {
    e.stopPropagation();
    setTestNotification(notification);
    setTestEmail(user?.email || '');
    setTestPhone('');
    setTestStep(1);  // Reset to step 1
    // Default to email if available, otherwise sms
    setTestChannel(hasEmail(notification) ? 'email' : 'sms');
    setShowTestDialog(true);
  };

  const handleSendTest = async () => {
    if (testChannel === 'email' && !testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    if (testChannel === 'sms' && !testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    try {
      setSendingTest(true);
      
      // Different endpoints for drip campaigns vs instant triggers
      const endpoint = testNotification.isMultiStep
        ? `${API}/platform/drip-campaigns/${testNotification.id}/test`
        : `${API}/platform/email-triggers/${testNotification.id}/test`;
      
      const requestBody = {
        channel: testChannel,
        recipient_email: testChannel === 'email' ? testEmail : null,
        recipient_phone: testChannel === 'sms' ? testPhone : null
      };
      
      // Include step for drip campaigns
      if (testNotification.isMultiStep) {
        requestBody.step = testStep;
      }
      
      const response = await axios.post(endpoint, requestBody, getAuthConfig());
      
      const results = response.data.results;
      
      // Show appropriate feedback
      if (testChannel === 'email') {
        // For drip campaigns, check the specific step result
        const emailResult = testNotification.isMultiStep 
          ? results[`email_${testStep}`] 
          : (results.email || results.email_1);
        if (emailResult?.success) {
          toast.success(`Test email (Step ${testStep}) sent to ${emailResult.sent_to}`);
        } else if (emailResult === null) {
          toast.warning(`No email template configured for Step ${testStep}`);
        } else {
          toast.error(`Email failed: ${emailResult?.error || 'Unknown error'}`);
        }
      } else {
        const smsResult = testNotification.isMultiStep 
          ? results[`sms_${testStep}`] 
          : (results.sms || results.sms_1);
        if (smsResult?.success) {
          toast.success(`Test SMS (Step ${testStep}) sent to ${smsResult.sent_to}`);
        } else if (smsResult === null) {
          toast.warning(`No SMS message configured for Step ${testStep}`);
        } else {
          toast.error(`SMS failed: ${smsResult?.error || 'Unknown error'}`);
        }
      }
      
      setShowTestDialog(false);
    } catch (error) {
      console.error('Failed to send test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setSendingTest(false);
    }
  };

  const filteredNotifications = filterCategory === 'all' 
    ? notifications 
    : notifications.filter(n => n.category === filterCategory);

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin/comms">
      <div className="space-y-6" data-testid="comms-notifications">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Notifications</h1>
            <p className="text-sm text-zinc-500">Configure when and how messages are sent</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={syncTriggers}
              disabled={syncing}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              title="Sync notification types with latest defaults"
              data-testid="btn-sync-triggers"
            >
              <Download className={`w-4 h-4 mr-2 ${syncing ? 'animate-pulse' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync'}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadNotifications}
              disabled={loading}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="btn-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button
            variant={filterCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterCategory('all')}
            className={filterCategory === 'all' ? 'bg-amber-500 text-zinc-900' : 'border-zinc-700 text-zinc-400'}
          >
            All
          </Button>
          {CATEGORIES.map(cat => (
            <Button
              key={cat}
              variant={filterCategory === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterCategory(cat)}
              className={filterCategory === cat ? 'bg-amber-500 text-zinc-900' : 'border-zinc-700 text-zinc-400'}
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading...
          </div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map(notification => {
              const Icon = notification.icon || Bell;
              const TypeIcon = getTypeIcon(notification.type);
              const active = isActive(notification);
              const emailConfigured = hasEmail(notification);
              const smsConfigured = hasSms(notification);
              
              return (
                <Card 
                  key={notification.id}
                  className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                  onClick={() => handleEdit(notification)}
                  data-testid={`notification-${notification.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Icon + Info */}
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          active ? 'bg-emerald-500/10' : 'bg-zinc-800'
                        }`}>
                          <Icon className={`w-5 h-5 ${active ? 'text-emerald-400' : 'text-zinc-500'}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-zinc-100 text-sm">{notification.name}</h3>
                            {notification.essential && (
                              <Badge className="bg-zinc-700 text-zinc-400 text-[10px]">Essential</Badge>
                            )}
                          </div>
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                            {notification.description || 'Configure this notification'}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`${getTypeBadgeStyle(notification.type)} text-[10px]`}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {notification.type}
                            </Badge>
                            <span className="text-[10px] text-zinc-600">•</span>
                            <span className="text-[10px] text-zinc-500">{notification.category}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right: Channel Status + Arrow */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Email indicator */}
                        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                          emailConfigured && (notification.isMultiStep ? notification.is_enabled : notification.email_enabled)
                            ? 'bg-blue-500/20 text-blue-400'
                            : emailConfigured
                              ? 'bg-zinc-800 text-zinc-500'
                              : 'bg-zinc-900 text-zinc-600'
                        }`} title={emailConfigured ? 'Email configured' : 'No email template'}>
                          <Mail className="w-3 h-3" />
                        </div>
                        
                        {/* SMS indicator */}
                        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                          smsConfigured && (notification.isMultiStep ? notification.is_enabled : notification.sms_enabled)
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : smsConfigured
                              ? 'bg-zinc-800 text-zinc-500'
                              : 'bg-zinc-900 text-zinc-600'
                        }`} title={smsConfigured ? 'SMS configured' : 'No SMS message'}>
                          <MessageSquare className="w-3 h-3" />
                        </div>
                        
                        {/* Status */}
                        <Badge className={`text-[10px] ${
                          active 
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                            : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                        }`}>
                          {active ? 'Active' : 'Paused'}
                        </Badge>
                        
                        {/* Send Test Button */}
                        {(emailConfigured || smsConfigured) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => openTestDialog(notification, e)}
                            className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-amber-900/20"
                            title="Send test notification"
                            data-testid={`btn-test-${notification.id}`}
                          >
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        
                        {/* Broadcast to Season Button (for manual triggers) */}
                        {notification.type === 'Manual' && (emailConfigured || smsConfigured) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => openBroadcastDialog(notification, e)}
                            className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
                            title="Send to all players in a season"
                            data-testid={`btn-broadcast-${notification.id}`}
                          >
                            <Users className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        
                        <Pencil className="w-4 h-4 text-zinc-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              {selectedNotification?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {selectedNotification?.isMultiStep 
                ? 'Multi-step notification with configurable timing'
                : selectedNotification?.variables?.description || 'Configure message delivery'
              }
            </DialogDescription>
          </DialogHeader>

          {selectedNotification?.isMultiStep ? (
            /* Multi-Step (Drip) Editor with Tabs */
            <Tabs defaultValue="email" className="mt-4">
              <TabsList className="bg-zinc-800 border border-zinc-700 w-full">
                <TabsTrigger value="email" className="flex-1 data-[state=active]:bg-blue-600">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="flex-1 data-[state=active]:bg-emerald-600">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  SMS
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 data-[state=active]:bg-zinc-600">
                  Settings
                </TabsTrigger>
              </TabsList>

              {/* Email Tab */}
              <TabsContent value="email" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <div>
                      <Label className="text-zinc-100">Email Notifications</Label>
                      <p className="text-xs text-zinc-500">Send via SendGrid</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.email_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, email_enabled: checked })}
                    data-testid="switch-email-enabled"
                  />
                </div>

                {formData.email_enabled && formData.steps?.map((step, idx) => (
                  <Card key={idx} className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-zinc-100 text-sm">Step {step.step} Email</h4>
                      </div>
                      <div>
                        <Label className="text-zinc-300 text-sm">SendGrid Template ID</Label>
                        <Input
                          value={step.email_template_id}
                          onChange={(e) => updateStep(idx, 'email_template_id', e.target.value)}
                          placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
                          className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100 font-mono text-sm"
                          data-testid={`input-step-${idx + 1}-email-template`}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {formData.email_enabled && (
                  <a 
                    href="https://mc.sendgrid.com/dynamic-templates" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs"
                  >
                    Open SendGrid Templates
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </TabsContent>

              {/* SMS Tab */}
              <TabsContent value="sms" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-emerald-400" />
                    <div>
                      <Label className="text-zinc-100">SMS Notifications</Label>
                      <p className="text-xs text-zinc-500">Send via Twilio</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.sms_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, sms_enabled: checked })}
                    data-testid="switch-sms-enabled"
                  />
                </div>

                {formData.sms_enabled && formData.steps?.map((step, idx) => (
                  <Card key={idx} className="bg-zinc-800 border-zinc-700">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-zinc-100 text-sm">Step {step.step} SMS</h4>
                        <span className={`text-xs ${(step.sms_message?.length || 0) > SMS_CHAR_LIMIT ? 'text-red-400' : 'text-zinc-500'}`}>
                          {step.sms_message?.length || 0}/{SMS_CHAR_LIMIT}
                        </span>
                      </div>
                      <Textarea
                        value={step.sms_message}
                        onChange={(e) => updateStep(idx, 'sms_message', e.target.value)}
                        placeholder="Hey {{user_name}}! Don't forget to..."
                        className="bg-zinc-900 border-zinc-700 text-zinc-100 min-h-20 font-mono text-sm"
                        data-testid={`textarea-step-${idx + 1}-sms`}
                      />
                    </CardContent>
                  </Card>
                ))}

                {formData.sms_enabled && selectedNotification?.rawData?.variables?.length > 0 && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Available Variables</Label>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedNotification.rawData.variables.map((v) => (
                        <code
                          key={v.name}
                          className="px-2 py-1 bg-zinc-800 rounded text-xs text-emerald-400 border border-zinc-700"
                          title={v.description}
                        >
                          {`{{${v.name}}}`}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div>
                    <Label className="text-zinc-100">Campaign Enabled</Label>
                    <p className="text-xs text-zinc-500">Activate this notification sequence</p>
                  </div>
                  <Switch
                    checked={formData.is_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                    data-testid="switch-campaign-enabled"
                  />
                </div>

                <div>
                  <Label className="text-zinc-300">Message Timing</Label>
                  <div className="space-y-3 mt-2">
                    {formData.steps?.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg">
                        <span className="text-zinc-400 text-sm min-w-16">Step {step.step}:</span>
                        <span className="text-zinc-500 text-sm">After</span>
                        <Input
                          type="number"
                          min="1"
                          value={step.delay_days}
                          onChange={(e) => updateStep(idx, 'delay_days', parseInt(e.target.value) || 1)}
                          className="w-16 bg-zinc-900 border-zinc-700 text-zinc-100 text-center"
                          data-testid={`input-step-${idx + 1}-delay`}
                        />
                        <span className="text-zinc-500 text-sm">
                          {idx === 0 ? 'days in status' : 'days after Step 1'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedNotification?.rawData?.variables?.length > 0 && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Available Variables</Label>
                    <div className="bg-zinc-800 rounded-lg p-3 border border-zinc-700 mt-2 max-h-40 overflow-y-auto space-y-1">
                      {selectedNotification.rawData.variables.map((v) => (
                        <div key={v.name} className="flex items-center justify-between">
                          <div>
                            <code className="text-emerald-400 text-xs">{`{{${v.name}}}`}</code>
                            <p className="text-xs text-zinc-500">{v.description}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(`{{${v.name}}}`)}
                            className="text-zinc-500 hover:text-zinc-100 h-6 w-6 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            /* Instant/Scheduled Editor */
            <Tabs defaultValue="email" className="mt-4">
              <TabsList className="bg-zinc-800 border border-zinc-700 w-full">
                <TabsTrigger value="email" className="flex-1 data-[state=active]:bg-blue-600">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="sms" className="flex-1 data-[state=active]:bg-emerald-600">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  SMS
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 data-[state=active]:bg-zinc-600">
                  Settings
                </TabsTrigger>
              </TabsList>

              {/* Email Tab */}
              <TabsContent value="email" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <div>
                      <Label className="text-zinc-100">Email Notifications</Label>
                      <p className="text-xs text-zinc-500">Send via SendGrid</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.email_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, email_enabled: checked })}
                    disabled={!formData.template_id}
                  />
                </div>

                <div>
                  <Label className="text-zinc-300">SendGrid Template ID</Label>
                  <Input
                    value={formData.template_id}
                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                    placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100 font-mono"
                  />
                  <a 
                    href="https://mc.sendgrid.com/dynamic-templates" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs mt-2"
                  >
                    Open SendGrid Templates
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </TabsContent>

              {/* SMS Tab */}
              <TabsContent value="sms" className="space-y-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-emerald-400" />
                    <div>
                      <Label className="text-zinc-100">SMS Notifications</Label>
                      <p className="text-xs text-zinc-500">Send via Twilio</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.sms_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, sms_enabled: checked })}
                    disabled={!formData.sms_message?.trim()}
                  />
                </div>

                <div>
                  <div className="flex justify-between">
                    <Label className="text-zinc-300">SMS Message</Label>
                    <span className={`text-xs ${(formData.sms_message?.length || 0) > SMS_CHAR_LIMIT ? 'text-red-400' : 'text-zinc-500'}`}>
                      {formData.sms_message?.length || 0}/{SMS_CHAR_LIMIT}
                    </span>
                  </div>
                  <Textarea
                    value={formData.sms_message}
                    onChange={(e) => setFormData({ ...formData, sms_message: e.target.value })}
                    placeholder="Hey {{player_name}}! ..."
                    className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100 min-h-24 font-mono text-sm"
                  />
                </div>

                {selectedNotification?.variables?.variables?.length > 0 && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Insert Variable</Label>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedNotification.variables.variables.map((v) => (
                        <Button
                          key={v.name}
                          variant="outline"
                          size="sm"
                          onClick={() => insertVariable(v.name)}
                          className="text-xs bg-zinc-800 border-zinc-700 text-emerald-400 hover:bg-emerald-900/30"
                        >
                          {`{{${v.name}}}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Send To</Label>
                  <Select
                    value={formData.send_to}
                    onValueChange={(value) => setFormData({ ...formData, send_to: value })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="affected_player">Affected Player Only</SelectItem>
                      <SelectItem value="all_players">All Players in League</SelectItem>
                      <SelectItem value="all_players_in_season">All Players in Season</SelectItem>
                      <SelectItem value="admins">League Admins Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-zinc-300">Delay (minutes)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.delay_minutes}
                    onChange={(e) => setFormData({ ...formData, delay_minutes: parseInt(e.target.value) || 0 })}
                    className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
                  />
                  <p className="text-xs text-zinc-500 mt-1">0 = immediate delivery</p>
                </div>

                {selectedNotification?.variables?.variables?.length > 0 && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Available Variables</Label>
                    <div className="bg-zinc-800 rounded-lg p-3 border border-zinc-700 mt-2 max-h-40 overflow-y-auto space-y-1">
                      {selectedNotification.variables.variables.map((v) => (
                        <div key={v.name} className="flex items-center justify-between">
                          <div>
                            <code className="text-emerald-400 text-xs">{`{{${v.name}}}`}</code>
                            <p className="text-xs text-zinc-500">{v.description}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(`{{${v.name}}}`)}
                            className="text-zinc-500 hover:text-zinc-100 h-6 w-6 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setShowEditDialog(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-zinc-900"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Send className="w-5 h-5 text-amber-500" />
              Send Test Notification
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Send a test {testNotification?.isMultiStep ? 'drip message' : 'notification'} to verify it works correctly
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Notification Name */}
            <div className="p-3 bg-zinc-800 rounded-lg">
              <p className="text-xs text-zinc-500">Testing</p>
              <p className="text-zinc-100 font-medium">{testNotification?.name}</p>
            </div>

            {/* Channel Selection */}
            <div>
              <Label className="text-zinc-300">Channel</Label>
              <div className="flex gap-2 mt-2">
                {hasEmail(testNotification) && (
                  <Button
                    variant={testChannel === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTestChannel('email')}
                    className={testChannel === 'email' 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}
                    data-testid="btn-test-channel-email"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                )}
                {hasSms(testNotification) && (
                  <Button
                    variant={testChannel === 'sms' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTestChannel('sms')}
                    className={testChannel === 'sms' 
                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}
                    data-testid="btn-test-channel-sms"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    SMS
                  </Button>
                )}
              </div>
            </div>

            {/* Recipient Input */}
            {testChannel === 'email' ? (
              <div>
                <Label className="text-zinc-300">Recipient Email</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 bg-zinc-800 border-zinc-700 text-zinc-100"
                  data-testid="input-test-email"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Sample data will be used for template variables
                </p>
              </div>
            ) : (
              <div>
                <Label className="text-zinc-300">Recipient Phone</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  <Input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+1234567890"
                    className="bg-zinc-800 border-zinc-700 text-zinc-100"
                    data-testid="input-test-phone"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  Include country code (e.g., +1 for US)
                </p>
              </div>
            )}

            {/* Step selector for multi-step notifications */}
            {testNotification?.isMultiStep && (
              <div>
                <Label className="text-zinc-300">Which Step to Test</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant={testStep === 1 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTestStep(1)}
                    className={testStep === 1 
                      ? 'bg-amber-600 hover:bg-amber-700' 
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}
                    data-testid="btn-test-step-1"
                  >
                    <Timer className="w-4 h-4 mr-2" />
                    Step 1
                  </Button>
                  <Button
                    variant={testStep === 2 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTestStep(2)}
                    className={testStep === 2 
                      ? 'bg-amber-600 hover:bg-amber-700' 
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}
                    data-testid="btn-test-step-2"
                  >
                    <Repeat className="w-4 h-4 mr-2" />
                    Step 2
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Each step can be tested individually to verify content
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button 
              variant="ghost" 
              onClick={() => setShowTestDialog(false)} 
              className="text-zinc-400"
              disabled={sendingTest}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendTest}
              disabled={sendingTest}
              className="bg-amber-500 hover:bg-amber-600 text-zinc-900"
              data-testid="btn-send-test"
            >
              {sendingTest ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast to Season Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              Broadcast to Seasons
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Send this notification to all players in selected seasons
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Notification Name */}
            <div className="p-3 bg-zinc-800 rounded-lg">
              <p className="text-xs text-zinc-500">Sending</p>
              <p className="text-zinc-100 font-medium">{broadcastNotification?.name}</p>
            </div>

            {/* Season Selection - Multi-select with checkboxes */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-300">Select Seasons</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectAll}
                  className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20 h-7 px-2"
                >
                  {broadcastSeasonIds.length === seasons.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg max-h-64 overflow-y-auto">
                {seasons.length === 0 ? (
                  <div className="p-4 text-center text-zinc-500">
                    No seasons found
                  </div>
                ) : (
                  seasons.map(season => {
                    const leagueName = season.tenant_name && season.tenant_name !== 'Unknown' 
                      ? season.tenant_name 
                      : `League ${season.tenant_id?.slice(0, 8) || 'Unknown'}`;
                    return (
                      <label
                        key={season.id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-700/50 border-b border-zinc-700/50 last:border-b-0 ${
                          broadcastSeasonIds.includes(season.id) ? 'bg-emerald-900/30' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={broadcastSeasonIds.includes(season.id)}
                          onChange={() => toggleSeasonSelection(season.id)}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-zinc-900 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-zinc-100 text-sm font-medium truncate">
                            {leagueName}
                          </p>
                          <p className="text-zinc-400 text-xs">
                            Season {season.number || '?'} • {season.status || 'unknown'}
                          </p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              
              <p className="text-xs text-zinc-500 mt-2">
                {broadcastSeasonIds.length === 0 
                  ? 'Select one or more seasons to broadcast to'
                  : `${broadcastSeasonIds.length} season${broadcastSeasonIds.length > 1 ? 's' : ''} selected`
                }
              </p>
            </div>

            {/* Channel Selection */}
            <div>
              <Label className="text-zinc-300">Channel</Label>
              <div className="flex gap-2 mt-2">
                {hasEmail(broadcastNotification) && (
                  <Button
                    variant={broadcastChannel === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBroadcastChannel('email')}
                    className={broadcastChannel === 'email' 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                )}
                {hasSms(broadcastNotification) && (
                  <Button
                    variant={broadcastChannel === 'sms' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBroadcastChannel('sms')}
                    className={broadcastChannel === 'sms' 
                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    SMS
                  </Button>
                )}
                <Button
                  variant={broadcastChannel === 'both' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBroadcastChannel('both')}
                  className={broadcastChannel === 'both' 
                    ? 'bg-purple-600 hover:bg-purple-700' 
                    : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}
                >
                  Both
                </Button>
              </div>
            </div>

            {/* Warning */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-amber-400 text-sm font-medium">⚠️ This will send immediately</p>
              <p className="text-amber-400/70 text-xs mt-1">
                Real emails/SMS will be sent to all players in {broadcastSeasonIds.length || 'selected'} season{broadcastSeasonIds.length !== 1 ? 's' : ''}. Make sure your template is ready.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button 
              variant="ghost" 
              onClick={() => setShowBroadcastDialog(false)} 
              className="text-zinc-400"
              disabled={sendingBroadcast}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBroadcast}
              disabled={sendingBroadcast || broadcastSeasonIds.length === 0}
              className="bg-emerald-500 hover:bg-emerald-600 text-zinc-900"
              data-testid="btn-send-broadcast"
            >
              {sendingBroadcast ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  Send to All Players
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
